import {
  Accordion,
  Badge,
  Box,
  Code,
  Divider,
  Group,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { User } from 'src/admin/users/types';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import TextInput from 'src/components/design-system/input/TextInput';
import { Company, useCompanies } from 'src/secret-menu/hooks/useCompanies';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type ConversationMessage = {
  role: string;
  content: string | null;
  tool_calls?: {
    id: string;
    function: { name: string; arguments: string };
  }[];
  tool_call_id?: string;
  name?: string;
  reasoning_content?: string;
  reasoningContent?: string;
};

type ConversationSummary = {
  id: string;
  userId: string;
  clientId: string | null;
  messageCount: number;
  startedAt: string;
  updatedAt: string;
  firstUserMessage: string | null;
};

const ROLE_COLORS: Record<string, string> = {
  system: 'gray',
  user: 'blue',
  assistant: 'orange',
  tool: 'teal',
};

function ToolCallDisplay({
  toolCall,
}: {
  toolCall: NonNullable<ConversationMessage['tool_calls']>[0];
}) {
  let parsedArgs: string;
  try {
    parsedArgs = JSON.stringify(JSON.parse(toolCall.function.arguments), null, 2);
  } catch {
    parsedArgs = toolCall.function.arguments;
  }

  return (
    <Box
      my="xs"
      p="xs"
      style={{ border: '1px solid var(--mantine-color-teal-2)', borderRadius: 8 }}
    >
      <Group gap="xs" mb="xxs">
        <Badge size="xs" color="teal" variant="light">
          tool call
        </Badge>
        <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="teal.7">
          {toolCall.function.name}
        </Text>
        <Text size="textXs" c="dimmed">
          {toolCall.id}
        </Text>
      </Group>
      <Code block style={{ fontSize: 11, maxHeight: 300, overflow: 'auto' }}>
        {parsedArgs}
      </Code>
    </Box>
  );
}

function MessageDisplay({ message, index }: { message: ConversationMessage; index: number }) {
  const roleColor = ROLE_COLORS[message.role] ?? 'gray';
  const thinkingContent = message.reasoning_content ?? message.reasoningContent;
  const hasThinking = !!thinkingContent;
  const isSystem = message.role === 'system';
  const isTool = message.role === 'tool';

  const contentPreview =
    isSystem && message.content && message.content.length > 500
      ? message.content.slice(0, 500) + '...'
      : null;

  return (
    <Box mb="md" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
      <Group gap="xs" mb="xs">
        <Badge size="sm" color={roleColor} variant="filled">
          {message.role}
        </Badge>
        <Text size="textXs" c="dimmed">
          #{index}
        </Text>
        {isTool && message.name && (
          <Badge size="xs" color="teal" variant="light">
            {message.name}
          </Badge>
        )}
      </Group>

      {hasThinking && (
        <Accordion variant="contained" mb="xs">
          <Accordion.Item value="thinking" role="region">
            <Accordion.Control>
              <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="violet.6">
                Model Thinking
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Box fz={12} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {thinkingContent}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}

      {isSystem && contentPreview ? (
        <Accordion variant="contained">
          <Accordion.Item value="system-prompt" role="region">
            <Accordion.Control>
              <Text size="textXs" c="dimmed">
                System prompt ({message.content!.length.toLocaleString()} chars)
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Box fz={12} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {message.content}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      ) : message.content ? (
        <Box fz={13}>
          <MarkdownView value={message.content} />
        </Box>
      ) : null}

      {message.tool_calls?.map(tc => (
        <ToolCallDisplay key={tc.id} toolCall={tc} />
      ))}
    </Box>
  );
}

function ConversationMessages({
  conversationId,
  tenantName,
}: {
  conversationId: string;
  tenantName: string;
}) {
  const { data, isLoading } = useQuery<{ messages: ConversationMessage[] }>({
    queryKey: ['admin', 'conversations', conversationId, 'debug', { tenantName }],
    enabled: !!conversationId,
  });

  const messages = data?.messages ?? [];

  return (
    <ScrollArea h="60vh">
      {isLoading && (
        <Text size="textSm" c="dimmed" ta="center" py="xl">
          Loading conversation...
        </Text>
      )}
      {!isLoading && messages.length === 0 && (
        <Text size="textSm" c="dimmed" ta="center" py="xl">
          No messages found.
        </Text>
      )}
      {messages.map((msg, i) => (
        <MessageDisplay key={i} message={msg} index={i} />
      ))}
    </ScrollArea>
  );
}

function BrowseTab() {
  const { companies, isLoading: companiesLoading } = useCompanies();
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const { data: users } = useQuery<User[]>({
    queryKey: ['admin', 'companies', selectedCompany?.id, 'users'],
    enabled: !!selectedCompany,
  });

  const { data: conversations } = useQuery<ConversationSummary[]>({
    queryKey: [
      'admin',
      'conversations',
      'by-user',
      selectedUserId,
      { tenantName: selectedCompany?.tenantName },
    ],
    enabled: !!selectedUserId && !!selectedCompany,
  });

  const companyOptions = (companies ?? []).map(c => ({
    value: c.id,
    label: `${c.name} (${c.tenantName})`,
  }));

  const userOptions = (users ?? []).map(u => ({
    value: u.id,
    label: `${u.firstName} ${u.lastName} (${u.emailAddress})`,
  }));

  const conversationOptions = (conversations ?? []).map(c => ({
    value: c.id,
    label: `${c.firstUserMessage ?? '(no user message)'} — ${new Date(c.updatedAt).toLocaleString()} (${c.messageCount} msgs)`,
  }));

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label="Company"
          placeholder="Select a company"
          data={companyOptions}
          searchable
          clearable
          disabled={companiesLoading}
          value={selectedCompany?.id ?? null}
          onChange={val => {
            const company = companies?.find(c => c.id === val) ?? null;
            setSelectedCompany(company);
            setSelectedUserId(null);
            setSelectedConversationId(null);
          }}
        />
        <Select
          label="User"
          placeholder="Select a user"
          data={userOptions}
          searchable
          clearable
          disabled={!selectedCompany}
          value={selectedUserId}
          onChange={val => {
            setSelectedUserId(val);
            setSelectedConversationId(null);
          }}
        />
      </Group>

      {conversationOptions.length > 0 && (
        <Select
          label="Conversation"
          placeholder="Select a conversation"
          data={conversationOptions}
          searchable
          clearable
          value={selectedConversationId}
          onChange={setSelectedConversationId}
        />
      )}

      {selectedUserId && conversations && conversations.length === 0 && (
        <Text size="textSm" c="dimmed">
          No conversations found for this user.
        </Text>
      )}

      {selectedConversationId && selectedCompany && (
        <>
          <Divider />
          <ConversationMessages
            conversationId={selectedConversationId}
            tenantName={selectedCompany.tenantName}
          />
        </>
      )}
    </Stack>
  );
}

function SearchTab() {
  const [conversationId, setConversationId] = useState('');
  const [tenantName, setTenantName] = useState('');
  const { companies } = useCompanies();

  const companyOptions = (companies ?? []).map(c => ({
    value: c.tenantName,
    label: `${c.name} (${c.tenantName})`,
  }));

  const canSearch = conversationId.length === 36 && tenantName.length > 0;

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label="Tenant"
          placeholder="Select tenant"
          data={companyOptions}
          searchable
          value={tenantName || null}
          onChange={val => setTenantName(val ?? '')}
        />
        <TextInput
          label="Conversation ID"
          placeholder="Paste a conversation UUID"
          value={conversationId}
          onChange={e => setConversationId(e.currentTarget.value.trim())}
        />
      </Group>

      {canSearch && (
        <>
          <Divider />
          <ConversationMessages conversationId={conversationId} tenantName={tenantName} />
        </>
      )}
    </Stack>
  );
}

export default function ConversationDebuggerTab() {
  return (
    <Stack p="md" gap="md">
      <Title order={3}>Conversation Debugger</Title>
      <Tabs defaultValue="browse">
        <Tabs.List>
          <Tabs.Tab value="browse">Browse</Tabs.Tab>
          <Tabs.Tab value="search">Search by ID</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="browse" pt="md">
          <BrowseTab />
        </Tabs.Panel>
        <Tabs.Panel value="search" pt="md">
          <SearchTab />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
